import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, fmtDateTime } from "@/lib/format";
import { DateFilter, buildParams } from "@/components/DateFilter";
import { printThermal, printA4, shareWhatsApp } from "@/lib/print";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Search, Eye, Printer, Share2, ReceiptText, Trash2, FileCheck2, Loader2, Repeat } from "lucide-react";
import { toast } from "sonner";

export default function BillHistory() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState({ period: "today" });
  const [search, setSearch] = useState("");
  const [method, setMethod] = useState("all");
  const [view, setView] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [gst, setGst] = useState(null);
  const [gstBusy, setGstBusy] = useState(false);
  const [transport, setTransport] = useState({ transport_mode: "1", vehicle_no: "", distance: "", transporter_id: "" });

  const { data: bills, isLoading } = useQuery({
    queryKey: ["bills", filter, search, method],
    queryFn: async () => (await api.get("/sales", { params: { ...buildParams(filter), search, payment_method: method } })).data,
  });
  const { data: settings } = useQuery({ queryKey: ["all-settings"], queryFn: async () => (await api.get("/settings")).data });

  const total = (bills || []).reduce((a, b) => a + (b.grand_total || 0), 0);

  async function del(id) {
    try { await api.delete(`/sales/${id}`); toast.success("Bill deleted"); setDeleteId(null); qc.invalidateQueries({ queryKey: ["bills"] }); }
    catch (e) { toast.error(apiError(e)); }
  }

  async function genEInvoice() {
    setGstBusy(true);
    try {
      const { data } = await api.post(`/gst/einvoice/${gst.id}`);
      toast.success("e-Invoice generated · IRN received");
      setGst({ ...gst, einvoice: data });
      qc.invalidateQueries({ queryKey: ["bills"] });
    } catch (e) { toast.error(apiError(e)); } finally { setGstBusy(false); }
  }
  async function genEWayBill() {
    setGstBusy(true);
    try {
      const { data } = await api.post(`/gst/ewaybill/${gst.id}`, { ...transport, distance: Number(transport.distance || 0) });
      toast.success("e-Way Bill generated");
      setGst({ ...gst, ewaybill: data });
      qc.invalidateQueries({ queryKey: ["bills"] });
    } catch (e) { toast.error(apiError(e)); } finally { setGstBusy(false); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#0F172A]">Bill History</h2>
          <p className="text-slate-500 text-sm">{(bills || []).length} bills · {money(total)}</p>
        </div>
        <DateFilter value={filter} onChange={setFilter} />
      </div>

      <Card className="p-4 border-slate-200">
        <div className="grid md:grid-cols-3 gap-3">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search bill no / customer..." className="pl-9" data-testid="bill-search" />
          </div>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger data-testid="bill-method-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white/95 backdrop-blur-md">
              <SelectItem value="all">All Payments</SelectItem>
              {["cash", "upi", "card", "credit", "partial"].map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Bill No</TableHead><TableHead>Date</TableHead><TableHead>Customer</TableHead>
              <TableHead className="text-right">Total</TableHead><TableHead>Payment</TableHead>
              <TableHead>GST</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {isLoading ? <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading...</TableCell></TableRow>
                : (bills || []).length ? bills.map((b) => (
                  <TableRow key={b.id} className="hover:bg-slate-50" data-testid={`bill-row-${b.bill_no}`}>
                    <TableCell className="font-mono font-medium text-[#1A2A5E]">{b.bill_no}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{fmtDateTime(b.created_at)}</TableCell>
                    <TableCell>{b.customer_name}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{money(b.grand_total)}</TableCell>
                    <TableCell><Badge variant="secondary" className="capitalize">{b.payment_method || "—"}</Badge></TableCell>
                    <TableCell>{b.einvoice?.status === "generated" ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">IRN</Badge> : b.einvoice?.status === "cancelled" ? <Badge variant="secondary">Cancelled</Badge> : <span className="text-slate-300 text-xs">—</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setView(b)} data-testid={`view-bill-${b.bill_no}`}><Eye className="h-4 w-4 text-slate-500" /></Button>
                        {b.doc_type === "detailed" && hasPerm("billing") && <Button size="icon" variant="ghost" className="h-8 w-8" title="Repeat this invoice" onClick={() => navigate(`/detailed-invoice?repeat=${b.id}`)} data-testid={`repeat-bill-${b.bill_no}`}><Repeat className="h-4 w-4 text-purple-600" /></Button>}
                        {hasPerm("gst_compliance") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setGst(b); setTransport({ transport_mode: "1", vehicle_no: "", distance: "", transporter_id: "" }); }} data-testid={`gst-bill-${b.bill_no}`}><FileCheck2 className="h-4 w-4 text-[#1A2A5E]" /></Button>}
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => printThermal(b, settings?.business, settings?.bill, settings?.printer)}><Printer className="h-4 w-4 text-slate-500" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => shareWhatsApp(b, settings?.business)}><Share2 className="h-4 w-4 text-emerald-600" /></Button>
                        {hasPerm("delete_bill") && <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDeleteId(b.id)} data-testid={`delete-bill-${b.bill_no}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>}
                      </div>
                    </TableCell>
                  </TableRow>
                )) : <TableRow><TableCell colSpan={7} className="text-center py-12 text-slate-400"><ReceiptText className="h-8 w-8 mx-auto mb-2 opacity-40" />No bills found</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E]">Bill {view?.bill_no}</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-3">
              <div className="text-sm text-slate-500">{view.customer_name} · {fmtDateTime(view.created_at)}</div>
              <div className="divide-y divide-slate-100 border-y">
                {view.items.map((it, i) => (
                  <div key={i} className="flex justify-between py-2 text-sm">
                    <span>{it.name} <span className="text-slate-400">x{it.qty}</span></span>
                    <span className="font-mono">{money(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-mono">{money(view.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">GST</span><span className="font-mono">{money(view.tax_total)}</span></div>
                <div className="flex justify-between font-bold text-[#1A2A5E] pt-1 border-t"><span>Total</span><span className="font-mono">{money(view.grand_total)}</span></div>
              </div>
              {view.doc_type === "detailed" && hasPerm("billing") && <Button className="w-full bg-purple-700 hover:bg-purple-800" onClick={() => navigate(`/detailed-invoice?repeat=${view.id}`)} data-testid="repeat-invoice-btn"><Repeat className="h-4 w-4 mr-1.5" />Repeat this Invoice</Button>}
              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" onClick={() => printThermal(view, settings?.business, settings?.bill, settings?.printer)}><Printer className="h-4 w-4 mr-1.5" /> Print</Button>
                <Button variant="outline" onClick={() => printA4(view, settings?.business, settings?.bill)}>A4</Button>
                <Button variant="outline" onClick={() => shareWhatsApp(view, settings?.business)}><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!gst} onOpenChange={() => setGst(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader><DialogTitle className="font-display text-[#1A2A5E] flex items-center gap-2"><FileCheck2 className="h-5 w-5" />GST · {gst?.bill_no}</DialogTitle></DialogHeader>
          {gst && (
            <div className="space-y-4" data-testid="gst-dialog">
              <div className="rounded-lg border p-3 text-sm space-y-1">
                <div className="font-semibold text-slate-700">e-Invoice</div>
                {gst.einvoice?.status === "generated" ? (
                  <div className="text-xs text-slate-600 break-all">
                    <div><b>IRN:</b> {gst.einvoice.irn}</div>
                    {gst.einvoice.ack_no && <div><b>Ack No:</b> {gst.einvoice.ack_no}</div>}
                    {gst.einvoice.ack_dt && <div><b>Ack Dt:</b> {gst.einvoice.ack_dt}</div>}
                  </div>
                ) : gst.einvoice?.status === "cancelled" ? <div className="text-xs text-amber-600">Cancelled</div>
                  : <Button size="sm" className="bg-[#1A2A5E]" disabled={gstBusy} onClick={genEInvoice} data-testid="gen-einvoice">{gstBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate e-Invoice"}</Button>}
              </div>

              <div className="rounded-lg border p-3 text-sm space-y-2">
                <div className="font-semibold text-slate-700">e-Way Bill</div>
                {gst.ewaybill?.status === "generated" ? (
                  <div className="text-xs text-slate-600"><div><b>EWB No:</b> {gst.ewaybill.ewb_no}</div>{gst.ewaybill.valid_upto && <div><b>Valid till:</b> {gst.ewaybill.valid_upto}</div>}</div>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">Vehicle No</Label><Input value={transport.vehicle_no} onChange={(e) => setTransport({ ...transport, vehicle_no: e.target.value })} className="h-8 mt-1" data-testid="ewb-vehicle" /></div>
                      <div><Label className="text-xs">Distance (km)</Label><Input type="number" value={transport.distance} onChange={(e) => setTransport({ ...transport, distance: e.target.value })} className="h-8 mt-1" /></div>
                    </div>
                    <Button size="sm" variant="outline" disabled={gstBusy} onClick={genEWayBill} data-testid="gen-ewaybill">{gstBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate e-Way Bill"}</Button>
                  </>
                )}
              </div>
              <p className="text-[11px] text-slate-400">Documents are only marked generated after the official GST provider API confirms them.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this bill?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the bill and restores its stock. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={() => del(deleteId)} data-testid="confirm-delete-bill">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
